import { withErrorBoundary } from 'app/components/error-boundary';
import { Column, Row } from 'app/design/primitives/box';
import { Calendar } from 'app/design/primitives/calendar';
import { Checkbox } from 'app/design/primitives/checkbox';
import { Page } from 'app/design/primitives/page';
import { Text } from 'app/design/primitives/text';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useNavigationOptions } from 'app/hooks/navigation';
import type { NavigationScreen } from 'app/types/navigation';
import { isSameDay } from 'app/lib/date';
import { usePageTitle } from 'app/utils/web-helpers';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import React, { useEffect } from 'react';
import { Accessory } from 'app/design/primitives/page/accessory';
import { Button } from 'app/design/primitives/button';

import { useUnavailableScreen } from './use-unavailable';
import { createTheme as createDSTheme } from 'app/shopify';
const theme = createDSTheme();

const UnavailableScreenComponent: NavigationScreen<'unavailable'> = ({ navigation }) => {
  const { t } = useScopedTranslation();

  const {
    today,
    maxSelectableMonthStart,
    currentMonthStart,
    isUnavailableDay,
    selectedStartDate,
    selectedEndDate,
    suspendNotificationsDuringUnavailability,
    setSuspendNotificationsDuringUnavailability,
    willNowChoose,
    onDatePress,
    saveForm,
    clearForm,
    isSaving,
    isDeleting,
    isLoadingProfile,
    hasPersistedUnavailability,
    canSave,
  } = useUnavailableScreen({
    onSaveSuccess: () => navigation.goBack(),
    onDeleteSuccess: () => navigation.goBack(),
  });

  useNavigationOptions(navigation, {
    headerTitle: () => (
      <Text variant="bodyXLargeMedium" color="white-label.header.on-header">
        {t('title')}
      </Text>
    ),
  });

  const setPageTitle = usePageTitle('unavailable');
  useEffect(() => setPageTitle('title'), [setPageTitle]);

  return (
    <Page
      accessory={
        <Accessory
          style={{
            gap: theme.spacing['spacing.general.x2'],
            marginHorizontal: theme.spacing['spacing.general.x4'],
          }}
        >
          {hasPersistedUnavailability ? (
            <Button
              disabled={isSaving || isDeleting}
              loading={isDeleting}
              onPress={() => void clearForm()}
              testID="unavailable-clear-button"
              variant="secondary"
            >
              {t('clearButtonLabel')}
            </Button>
          ) : null}
          <Button
            disabled={!canSave || isDeleting}
            loading={isSaving}
            onPress={() => void saveForm()}
            testID="unavailable-save-button"
            variant="primary"
          >
            {t('saveButtonLabel')}
          </Button>
        </Accessory>
      }
    >
      <Column gap="spacing.general.x4">
        <Text fontWeight="bold">{t('topInformation')}</Text>

        <Calendar
          initial={today}
          maxCurrent={maxSelectableMonthStart}
          minCurrent={currentMonthStart}
          onDatePress={onDatePress}
          disabled={isLoadingProfile || isSaving || isDeleting}
          selected={[selectedStartDate, selectedEndDate].filter((d): d is Date => Boolean(d))}
          isUnavailableDay={isUnavailableDay}
          willNowChoose={willNowChoose}
        />

        <Row alignItems="center" gap="spacing.general.x2">
          <Checkbox
            checked={suspendNotificationsDuringUnavailability}
            onPress={() => setSuspendNotificationsDuringUnavailability((v) => !v)}
          />
          <Text flex={1}>{t('suspendNotificationsLabel')}</Text>
        </Row>

        {!hasPersistedUnavailability && !canSave ? (
          <Text color="text.secondary" variant="bodyBase">
            {t('selectRangeHint')}
          </Text>
        ) : null}

        {!hasPersistedUnavailability &&
        canSave &&
        selectedStartDate &&
        selectedEndDate &&
        isSameDay(selectedStartDate, selectedEndDate) ? (
          <Text color="text.secondary" variant="bodyBase">
            {t('singleDayRangeHint')}
          </Text>
        ) : null}

        {hasPersistedUnavailability ? (
          <Text color="text.secondary" variant="bodyBase">
            {t('clearHint')}
          </Text>
        ) : null}
      </Column>
    </Page>
  );
};

export const UnavailableScreen: NavigationScreen<'unavailable'> = withOptionalMetrics(
  'UnavailableScreen',
  withErrorBoundary(UnavailableScreenComponent),
);
